import type { ApiSchema } from "../../../api/apiSchema";
import type { ListedProjectItem } from "../../../api/content/authoring/processAuthoringApi";

/**
 * A new skill as the form edits it (processes.md P-4, the Agent Skills format): a name the
 * agent sees, a one-line description that tells it when to use the skill, the SKILL.md
 * body, and files bundled from reviewed items. A bundled file is a tool version, pinned,
 * or an artifact read as of each Thread's content version; the skill never copies them.
 */

export type SkillContent = ApiSchema<"SkillContent">;
type BundledFile = SkillContent["files"][number];

export interface BundledFileRow {
  readonly path: string;
  readonly source: "tool" | "artifact";
  /** A tool's `VersionRef`, or an artifact's item ID. */
  readonly item: string;
}

export interface SkillDraft {
  readonly name: string;
  readonly description: string;
  readonly body: string;
  readonly files: readonly BundledFileRow[];
}

export const NEW_SKILL: SkillDraft = { name: "", description: "", body: "", files: [] };

export type SkillDraftProblem = "name_missing" | "description_missing" | "body_missing" | "file_item";

/**
 * What the form still lacks, in form order; empty when every field is filled. Whether the
 * skill is valid (its name's form, a description that fits the file's header, paths, limits) is
 * module B's definition check to say, as it says when the skill is saved (T5-07).
 */
export function skillDraftProblems(draft: SkillDraft): readonly SkillDraftProblem[] {
  const problems: SkillDraftProblem[] = [];
  if (draft.name === "") problems.push("name_missing");
  if (draft.description.trim() === "") problems.push("description_missing");
  if (draft.body.trim() === "") problems.push("body_missing");
  if (draft.files.some((file) => file.item === "")) problems.push("file_item");
  return problems;
}

/** The item content a change set takes; files in path order, as the server stores them. */
export function skillContent(draft: SkillDraft): SkillContent {
  const files = [...draft.files]
    .sort((left, right) => left.path.localeCompare(right.path))
    .map((file): BundledFile => (file.source === "tool" ? { path: file.path, tool: file.item } : { path: file.path, artifact: { item: file.item } }));
  return { kind: "skill", name: draft.name, description: draft.description.trim(), body: draft.body, files };
}

/**
 * A new row for a bundled item, at a path the agent recognises: a tool under `scripts/`
 * with its name, an artifact under `references/`. The path stays editable.
 */
export function bundledFileRow(item: ListedProjectItem): BundledFileRow {
  if (item.kind === "tool") {
    return { path: `scripts/${fileStem(item.name)}`, source: "tool", item: item.reference };
  }
  return { path: `references/${fileStem(item.name)}.md`, source: "artifact", item: item.itemId };
}

/** A file name from an item's name: lowercase words joined by hyphens, as paths allow. */
function fileStem(name: string): string {
  const stem = name
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9_.]+/g, "-")
    .replace(/^[-.]+|-+$/g, "");
  return stem === "" ? "file" : stem.slice(0, 90);
}
